// CelebrationOverlay — the dopamine pop for the user's first successful
// generation. Fires once per device (localStorage gate). Renders 64
// confetti chips arcing from a center burst, a brand mascot at scale,
// a "First app built" badge, and a short text confirmation. Tap or 4s
// timer dismisses; the overlay never blocks the underlying success UI.
//
// Reduced-motion fallback: confetti is suppressed; the badge appears
// statically and fades after 1.6s.

import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { t } from '../lib/i18n';
import type { JSX } from 'preact';
import { Sparkles } from 'lucide-preact';
import { PomelaMascot } from './PomelaMascot';

const LS_KEY_FIRST_BUILD = 'pomela:first-build-celebrated:v1';

/** Has the user already seen the first-build celebration? */
export function hasCelebratedFirstBuild(): boolean {
  try {
    return localStorage.getItem(LS_KEY_FIRST_BUILD) === '1';
  } catch {
    return false;
  }
}

/** Mark the first-build celebration as shown. */
export function markFirstBuildCelebrated(): void {
  try {
    localStorage.setItem(LS_KEY_FIRST_BUILD, '1');
  } catch {
    /* localStorage blocked — celebration may re-fire; harmless */
  }
}

interface ConfettiPiece {
  id: number;
  /** Final translateX in px from center. */
  dx: number;
  /** Final translateY in px from center. */
  dy: number;
  /** Final rotate in deg. */
  rot: number;
  /** Color from the brand palette. */
  color: string;
  /** Travel duration in ms. */
  dur: number;
  /** Stagger delay in ms. */
  delay: number;
  /** Width in px (height derived). */
  size: number;
}

// Confetti palette uses CSS custom-property references rather than hex
// literals so a brand-token change in styles.css cascades through here
// without a code edit. The vars resolve at paint time — Web Animations
// + DOM background read them per-instance, so each piece picks up the
// live palette (including dark-mode overrides). See styles.css `--color-*`.
const CONFETTI_TOKENS = [
  'var(--color-peel)',
  'var(--color-flesh)',
  'var(--color-seed)',
  'var(--color-zest)',
  'var(--color-bloom)'
] as const;

function generateConfetti(count: number): ConfettiPiece[] {
  const out: ConfettiPiece[] = [];
  for (let i = 0; i < count; i++) {
    const angle = (Math.PI * 2 * i) / count + Math.random() * 0.4 - 0.2;
    const distance = 180 + Math.random() * 220;
    out.push({
      id: i,
      dx: Math.cos(angle) * distance,
      dy: Math.sin(angle) * distance + Math.random() * 80, // gravity bias
      rot: Math.random() * 720 - 360,
      color: CONFETTI_TOKENS[i % CONFETTI_TOKENS.length] as string,
      dur: 1200 + Math.random() * 800,
      delay: Math.random() * 180,
      size: 6 + Math.random() * 6
    });
  }
  return out;
}

export interface CelebrationOverlayProps {
  /** Hands control back when the user dismisses or the auto-timer fires. */
  onDismiss: () => void;
  /** Override the headline (default: "Your first Pomela app is live."). */
  headline?: string;
  /** Override the sub line. */
  sub?: string;
}

export function CelebrationOverlay({ onDismiss, headline, sub }: CelebrationOverlayProps): JSX.Element {
  const [visible, setVisible] = useState(true);
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;
  // Capture the element that had focus when the celebration mounted so
  // we can return focus there on dismiss — keyboard users (and screen-
  // reader users navigating linearly) shouldn't lose their place
  // mid-celebration. WCAG 2.4.3.
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Honor prefers-reduced-motion at component mount; confetti is the
  // single biggest motion offender on this surface.
  const reduceMotion = useMemo(() => {
    try {
      return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    } catch {
      return false;
    }
  }, []);

  const pieces = useMemo(() => (reduceMotion ? [] : generateConfetti(64)), [reduceMotion]);

  useEffect(() => {
    // Snapshot focus before the overlay steals it. document.activeElement
    // can be null in headless contexts; the optional chain on restore
    // handles that gracefully.
    try {
      previousFocusRef.current = document.activeElement as HTMLElement | null;
    } catch {
      previousFocusRef.current = null;
    }
    navigator.vibrate?.([18, 40, 24, 60, 14]);
    const t = setTimeout(() => {
      setVisible(false);
      setTimeout(() => dismissRef.current(), 320);
    }, reduceMotion ? 1600 : 4200);
    return () => {
      clearTimeout(t);
      // Restore focus on unmount. The optional-call + try/catch matters
      // because the previously-focused element may have been removed
      // from the DOM during the celebration window.
      try {
        previousFocusRef.current?.focus?.();
      } catch {
        /* element no longer focusable — degrade silently */
      }
    };
  }, [reduceMotion]);

  function dismissNow(): void {
    setVisible(false);
    setTimeout(() => dismissRef.current(), 200);
  }

  return (
    <div
      className={`celebration${visible ? '' : ' celebration--leaving'}`}
      role="dialog"
      aria-live="polite"
      aria-label={t('cel.aria')}
      onClick={dismissNow}
    >
      <div className="celebration__scrim" aria-hidden="true" />

      {/* Confetti — absolute children burst outward from center. */}
      {pieces.map((p) => (
        <span
          key={p.id}
          className="celebration__piece"
          aria-hidden="true"
          style={{
            ['--dx' as string]: `${p.dx}px`,
            ['--dy' as string]: `${p.dy}px`,
            ['--rot' as string]: `${p.rot}deg`,
            ['--dur' as string]: `${p.dur}ms`,
            ['--delay' as string]: `${p.delay}ms`,
            background: p.color,
            width: `${p.size}px`,
            height: `${p.size * 1.6}px`
          }}
        />
      ))}

      <div className="celebration__stage">
        <div className="celebration__halo" aria-hidden="true" />
        <div className="celebration__mark">
          <PomelaMascot size={88} flat />
        </div>
        <div className="celebration__badge">
          <Sparkles size={13} strokeWidth={2.5} aria-hidden="true" />
          First app built
        </div>
        <h2 className="celebration__title">
          {headline ?? 'Your first Pomela app is live.'}
        </h2>
        <p className="celebration__sub">
          {sub ?? 'Tap anywhere to keep building. The next one is even faster.'}
        </p>
      </div>
    </div>
  );
}
